import { useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { InsightBadges, useActiveInsights } from '../components/InsightBadges';
import { duration, Stat } from '../components/Stat';
import { TeamBadge } from '../components/TeamBadge';
import { useUser } from '../app/session';
import { toast } from '../components/toasts';
import { Button, Empty, ErrorText, Spinner } from '../components/ui';
import { errorMessage, get, hasPermission, put } from '../lib/api';
import { PhoneOutgoing } from 'lucide-react';
import { useSoftphone } from '../app/softphone';
import { dateTimeOf, dialableNumber, formatPhone, since, timeOf } from '../lib/format';
import { useAttendanceSettings, usePresence } from '../lib/queries';
import { AgentView, Dashboard, WaitingCall } from '../lib/types';

/** Fila esperando há mais que isso fica em destaque (sem o alerta de fila parada configurado). */
const DEFAULT_WAIT_WARN_MS = 5 * 60_000;

/** Situação do operador na fila de ligações. */
function agentLabel(a: AgentView | undefined, now: number) {
  if (!a) return <span className="text-fg-soft">sem ramal</span>;
  if (a.status === 'paused') return <span className="font-medium text-warn">Pausa: {a.reason ?? '—'}{a.since && ` (${since(a.since, now)})`}</span>;
  if (a.inQueue) return <span className="text-ok">Na fila · ramal {a.extension}</span>;
  return <span className="text-fg-soft">Fora da fila{a.online ? '' : ' (offline)'}</span>;
}

/** Ligações de hoje e as perdidas que ainda esperam retorno (com botão para ligar de volta). */
function CallsToday({ data, now }: { data: Dashboard; now: number }) {
  const c = data.calls;
  const sp = useSoftphone();
  const canDial = sp.status === 'registered' && !sp.call;
  return (
    <>
      <h2 className="mb-2 text-xs font-semibold tracking-wide text-fg-soft uppercase">Ligações hoje</h2>
      <div className="mb-3 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <Stat label="Recebidas" value={c.received} />
        <Stat label="Atendidas" value={c.answered} tone="ok" />
        <Stat label="Perdidas" value={c.missed} tone={c.missed > 0 ? 'warn' : undefined} hint="O cliente ligou e ninguém atendeu" />
        <Stat label="Sem retorno" value={c.missedWithoutReturn} tone={c.missedWithoutReturn > 0 ? 'warn' : undefined} hint="Perdidas sem ligação de volta e sem nova ligação atendida do mesmo número" />
        <Stat label="Espera média na fila" value={duration(c.avgWaitSeconds)} hint={`Maior espera: ${duration(c.maxWaitSeconds)}`} />
        <Stat label="Feitas" value={c.made} hint={`${c.madeAnswered} atendidas pelo cliente`} />
        <Stat label="Em conversa" value={duration(c.talkSeconds)} hint="Soma do atender ao desligar (descreve as ligações; o tempo de atendimento é o das sessões)" />
      </div>
      {data.missedCalls.length > 0 && (
        <table className="mb-6 w-full overflow-hidden rounded-lg border border-border bg-panel text-sm">
          <thead className="bg-muted text-left text-xs text-fg-soft">
            <tr>
              <th className="px-3 py-2">Perdida sem retorno</th>
              <th className="px-3 py-2">Contato</th>
              <th className="px-3 py-2">Cliente</th>
              <th className="px-3 py-2">Esperou</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {data.missedCalls.map((m) => (
              <tr key={m.id} className="border-t border-border">
                <td className="px-3 py-2 whitespace-nowrap">
                  {dateTimeOf(m.startedAt)} <span className="text-xs text-fg-soft">(há {since(m.startedAt, now)})</span>
                </td>
                <td className="px-3 py-2">
                  {m.contact ?? <span className="text-fg-soft">sem nome</span>}
                  <div className="text-xs text-fg-soft">{m.phone ? formatPhone(m.phone) : 'número não identificado'}</div>
                </td>
                <td className="px-3 py-2">{m.customer ?? '—'}</td>
                <td className="px-3 py-2 tabular-nums">{m.waitSeconds !== null ? duration(m.waitSeconds) : '—'}</td>
                <td className="px-3 py-2 text-right">
                  {m.phone && (
                    <Button
                      size="sm"
                      variant="success"
                      disabled={!canDial}
                      title={canDial ? 'Ligar de volta pelo telefone da plataforma' : 'Telefone da plataforma desconectado ou em ligação'}
                      onClick={() => sp.engine.call(dialableNumber(m.phone))}
                    >
                      <PhoneOutgoing className="size-3.5" /> Ligar
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {data.missedCalls.length === 0 && <p className="mb-6 text-sm text-fg-soft">Nenhuma ligação perdida esperando retorno.</p>}
    </>
  );
}

/** Supervisão: clientes irritados ou impacientes agora (análise da IA), para o gestor agir. */
function UnhappyNow({ onOpen, now }: { onOpen: (attendanceId: string) => void; now: number }) {
  const insights = useActiveInsights();
  const unhappy = [...insights.values()]
    .filter((i) => i.mood === 'irritado' || i.mood === 'impaciente')
    .sort((a, b) => (a.mood === b.mood ? 0 : a.mood === 'irritado' ? -1 : 1));
  if (!unhappy.length) return null;
  return (
    <>
      <h2 className="mb-2 text-xs font-semibold tracking-wide text-fg-soft uppercase">Clientes insatisfeitos agora (IA)</h2>
      <table className="mb-6 w-full overflow-hidden rounded-lg border border-border bg-panel text-sm">
        <thead className="bg-muted text-left text-xs text-fg-soft">
          <tr>
            <th className="px-3 py-2">Contato</th>
            <th className="px-3 py-2">Humor</th>
            <th className="px-3 py-2">Com</th>
            <th className="px-3 py-2">Motivo</th>
            <th className="px-3 py-2">Analisado</th>
          </tr>
        </thead>
        <tbody>
          {unhappy.map((i) => (
            <tr key={i.attendanceId} className="cursor-pointer border-t border-border hover:bg-muted" onClick={() => onOpen(i.attendanceId)}>
              <td className="px-3 py-2">{i.contact ?? '—'}</td>
              <td className="px-3 py-2">
                <InsightBadges insight={{ ...i, urgency: 'baixa' }} />
              </td>
              <td className="px-3 py-2">{i.operator ?? <span className="text-fg-soft">na fila</span>}</td>
              <td className="px-3 py-2 text-xs">{i.reason ?? '—'}</td>
              <td className="px-3 py-2 text-xs whitespace-nowrap text-fg-soft">há {since(i.analyzedAt, now)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/** Relógio da tela (atualiza "há quanto tempo" e o tempo da sessão aberta). */
function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

/**
 * Painel em tempo real: fila por equipe, números de hoje e (supervisão) o que cada operador está
 * fazendo. Atualiza pelos avisos do servidor (realtime-bridge) e, por garantia, a cada 30 s.
 */
export function DashboardPage({ onOpen }: { onOpen: (attendanceId: string) => void }) {
  const { data, isLoading, error } = useQuery({ queryKey: ['dashboard'], queryFn: () => get<Dashboard>('/dashboard'), refetchInterval: 30_000 });
  const qc = useQueryClient();
  const canPause = hasPermission(useUser(), 'attendance:read_all');
  const setAgent = async (userId: string, status: 'available' | 'paused') => {
    try {
      await put(`/telephony/agents/${userId}/status`, status === 'paused' ? { status, reason: 'Pausado pela supervisão' } : { status });
      void qc.invalidateQueries({ queryKey: ['telephony'] });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  };
  const online = usePresence().data ?? new Set<string>();
  const now = useNow();
  // Telefonia: fila de ligações e disponível/pausa (vazio quando a telefonia está desligada).
  const waitingCalls = useQuery({ queryKey: ['calls', 'waiting'], queryFn: () => get<WaitingCall[]>('/telephony/queue/waiting'), refetchInterval: 30_000 }).data ?? [];
  const agents = useQuery({ queryKey: ['telephony', 'agents'], queryFn: () => get<AgentView[]>('/telephony/agents'), refetchInterval: 30_000 }).data ?? [];
  const agentOf = (userId: string) => agents.find((a) => a.userId === userId);
  const alertMinutes = useAttendanceSettings().data?.queueAlertMinutes ?? 0;
  const WAIT_WARN_MS = alertMinutes > 0 ? alertMinutes * 60_000 : DEFAULT_WAIT_WARN_MS;

  if (isLoading) return <Empty><Spinner /></Empty>;
  if (!data) return <div className="p-6"><ErrorText error={error} /></div>;

  const t = data.today;
  const sum = (k: 'bot' | 'queue' | 'inProgress' | 'transferred') => data.teams.reduce((acc, x) => acc + x[k], 0);
  const oldest = data.teams.map((x) => x.oldestQueuedAt).filter(Boolean).sort()[0] ?? null;
  const waitingLong = oldest !== null && now - Date.parse(oldest) > WAIT_WARN_MS;
  // Tempo de hoje: o servidor manda o valor na hora da consulta; a sessão aberta continua contando na tela.
  const elapsed = Math.max(0, Math.floor((now - Date.parse(data.generatedAt)) / 1000));

  return (
    <div className="mx-auto h-full max-w-6xl overflow-y-auto p-3 md:p-6">
      <div className="mb-4 flex items-baseline justify-between">
        <h1 className="text-lg font-semibold">Painel</h1>
        <span className="text-xs text-fg-soft">atualizado às {timeOf(data.generatedAt)}</span>
      </div>

      <h2 className="mb-2 text-xs font-semibold tracking-wide text-fg-soft uppercase">Agora</h2>
      <div className={clsx('mb-3 grid grid-cols-2 gap-3', agents.length ? 'md:grid-cols-6' : 'md:grid-cols-5')}>
        <Stat label="Com o robô" value={sum('bot')} hint="Clientes escolhendo a equipe no menu" />
        <Stat label="Na fila" value={sum('queue')} tone={sum('queue') > 0 ? 'warn' : undefined} />
        <Stat label="Esperando há" value={oldest ? since(oldest, now) : '—'} tone={waitingLong ? 'warn' : undefined} hint="O atendimento que está há mais tempo na fila" />
        <Stat label="Em atendimento" value={sum('inProgress')} tone="accent" />
        <Stat label="Transferidos" value={sum('transferred')} hint="Aguardando o operador de destino assumir" />
        {agents.length > 0 && (
          <Stat
            label="Ligações na fila"
            value={waitingCalls.length ? `${waitingCalls.length} · ${since(waitingCalls[0].queuedAt, now)}` : '0'}
            tone={waitingCalls.length ? 'warn' : undefined}
            hint="Clientes esperando na linha; ao lado, há quanto tempo espera o mais antigo"
          />
        )}
      </div>
      {data.teams.length > 0 && (
        <table className="mb-6 w-full overflow-hidden rounded-lg border border-border bg-panel text-sm">
          <thead className="bg-muted text-left text-xs text-fg-soft">
            <tr>
              <th className="px-3 py-2">Equipe</th>
              <th className="px-3 py-2 text-right">Com o robô</th>
              <th className="px-3 py-2 text-right">Na fila</th>
              <th className="px-3 py-2">Esperando há</th>
              <th className="px-3 py-2 text-right">Em atendimento</th>
              <th className="px-3 py-2 text-right">Transferidos</th>
            </tr>
          </thead>
          <tbody>
            {data.teams.map((x) => {
              const late = x.oldestQueuedAt !== null && now - Date.parse(x.oldestQueuedAt) > WAIT_WARN_MS;
              return (
                <tr key={x.teamId ?? 'none'} className="border-t border-border">
                  <td className="px-3 py-2">{x.team ? <TeamBadge name={x.team} /> : <TeamBadge name={null} showEmpty />}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{x.bot || '—'}</td>
                  <td className={clsx('px-3 py-2 text-right tabular-nums', x.queue > 0 && 'font-semibold')}>{x.queue || '—'}</td>
                  <td className={clsx('px-3 py-2', late && 'font-medium text-warn')}>{x.oldestQueuedAt ? since(x.oldestQueuedAt, now) : '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{x.inProgress || '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{x.transferred || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {data.teams.length === 0 && <p className="mb-6 text-sm text-fg-soft">Nenhum atendimento em andamento.</p>}

      {data.operators && <UnhappyNow onOpen={onOpen} now={now} />}

      <h2 className="mb-2 text-xs font-semibold tracking-wide text-fg-soft uppercase">Hoje</h2>
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-7">
        <Stat label="Recebidos" value={t.received} hint="Atendimentos iniciados por mensagem do cliente" />
        <Stat label="Iniciados por nós" value={t.startedByOperator} hint="Conversas iniciadas por um operador" />
        <Stat label="Finalizados" value={t.finished} tone="ok" />
        <Stat label="Cancelados" value={t.cancelled + t.spam} hint={`${t.cancelled} cancelados, ${t.spam} spam`} />
        <Stat label="Espera média" value={duration(t.avgWaitSeconds)} hint="Da chegada do cliente até um operador abrir o atendimento" />
        <Stat label="1ª resposta média" value={duration(t.avgFirstResponseSeconds)} hint="Da chegada do cliente até a primeira mensagem de um operador" />
        <Stat label="Tempo efetivo médio" value={duration(t.avgEffectiveSeconds)} hint="Soma das sessões de foco, nos atendimentos finalizados" />
      </div>

      {(agents.length > 0 || data.calls.received + data.calls.made > 0) && <CallsToday data={data} now={now} />}

      {data.operators && (
        <>
          <h2 className="mb-2 text-xs font-semibold tracking-wide text-fg-soft uppercase">Operadores</h2>
          <table className="w-full overflow-hidden rounded-lg border border-border bg-panel text-sm">
            <thead className="bg-muted text-left text-xs text-fg-soft">
              <tr>
                <th className="px-3 py-2">Operador</th>
                <th className="px-3 py-2 text-right">Em atendimento</th>
                <th className="px-3 py-2">Trabalhando agora em</th>
                {agents.length > 0 && <th className="px-3 py-2">Telefone</th>}
                <th className="px-3 py-2 text-right">Tempo efetivo hoje</th>
              </tr>
            </thead>
            <tbody>
              {[...data.operators]
                .sort((a, b) => Number(online.has(b.operatorId)) - Number(online.has(a.operatorId)) || a.name.localeCompare(b.name))
                .map((o) => {
                  const isOnline = online.has(o.operatorId);
                  return (
                    <tr key={o.operatorId} className={clsx('border-t border-border', !isOnline && !o.inProgress && 'text-fg-soft')}>
                      <td className="px-3 py-2">
                        <span className="flex items-center gap-2">
                          <span className={clsx('size-2 rounded-full', isOnline ? 'bg-ok' : 'bg-border')} title={isOnline ? 'online' : 'offline'} />
                          {o.name}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {o.inProgress || '—'}
                        {o.transferredToThem > 0 && <span className="ml-1 text-xs text-fg-soft">(+{o.transferredToThem} transferido)</span>}
                      </td>
                      <td className="px-3 py-2">
                        {o.focusAttendanceId ? (
                          <button className="text-left hover:underline" onClick={() => onOpen(o.focusAttendanceId!)}>
                            {o.focusContact ?? 'atendimento'} <span className="text-xs text-fg-soft">há {since(o.focusSince, now)}</span>
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                      {agents.length > 0 && <td className="px-3 py-2 text-xs">
                          <span className="flex items-center gap-2">
                            {agentLabel(agentOf(o.operatorId), now)}
                            {canPause && agentOf(o.operatorId) && (
                              <Button size="sm" variant="ghost" onClick={() => void setAgent(o.operatorId, agentOf(o.operatorId)!.status === 'paused' ? 'available' : 'paused')}>
                                {agentOf(o.operatorId)!.status === 'paused' ? 'Liberar' : 'Pausar'}
                              </Button>
                            )}
                          </span>
                        </td>}
                      <td className="px-3 py-2 text-right font-mono tabular-nums">{duration(o.effectiveTodaySeconds + (o.focusAttendanceId ? elapsed : 0))}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
